/**
 * SPILLESTEDET SETT FRA HUSET, QA15-S (agent A): leiepakkene per sal og husets egen kalender.
 *
 * Leiepakkene er radene i `venue_room_rates` (1168): én rad per pakke, bare husets, aldri gjennom en
 * port. Kalenderen er en lesning over kveldene (`venue_nights`) og køen (`venue_enquiries`), så et
 * hold fra et bud (C3) står her uten egen kode så lenge det ligger i køen.
 */
import { z } from 'zod';

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((value) => (value.length === 0 ? null : value))
    .nullish();

const money = z.number().int().min(0).max(1_000_000_000_00);

/* ------------------------------------------------------------------------------ leiepakkene */

export const venueRentPackageDtoSchema = z
  .object({
    id: z.uuid(),
    venueRoomId: z.uuid(),
    /** Null på den første pakken fra før 1168; skjermen kaller den «Standard». */
    name: z.string().nullable(),
    rentMinor: z.number().int().nullable(),
    currency: z.string(),
    /** Hva leien dekker: lyd, lys, tekniker, vakter … Fritekst. */
    included: z.string().nullable(),
    terms: z.string().nullable(),
    sortOrder: z.number().int(),
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: 'VenueRentPackage' });

export const venueRentPackageListSchema = z
  .object({ items: z.array(venueRentPackageDtoSchema) })
  .meta({ id: 'VenueRentPackageList' });

export const createVenueRentPackageRequestSchema = z
  .object({
    name: optionalText(120),
    rentMinor: money.nullish(),
    currency: z.string().trim().length(3).toUpperCase().optional(),
    included: optionalText(2000),
    terms: optionalText(4000),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
  })
  .meta({ id: 'CreateVenueRentPackageRequest' });

export const updateVenueRentPackageRequestSchema = createVenueRentPackageRequestSchema
  .refine((body) => Object.keys(body).length > 0, {
    message: 'At least one field must be present.',
  })
  .meta({ id: 'UpdateVenueRentPackageRequest' });

/* --------------------------------------------------------------------------- husets kalender */

export const VENUE_CALENDAR_ITEM_KINDS = ['event', 'hold', 'request'] as const;
export type VenueCalendarItemKind = (typeof VENUE_CALENDAR_ITEM_KINDS)[number];

/** Et vindu på høyst 62 dager: en måned med uka før og etter. */
const MAX_WINDOW_DAYS = 62;

export const venueCalendarQuerySchema = z
  .object({
    festivalId: z.uuid(),
    from: z.iso.date(),
    to: z.iso.date(),
    roomId: z.uuid().optional(),
    /** Tittel, innslag, arrangør, og hvem som står på vakt eller hvilken ressurs som er reservert. */
    q: z.string().trim().min(1).max(200).optional(),
  })
  .refine((query) => query.to >= query.from, { message: 'to_before_from', path: ['to'] })
  .refine(
    (query) =>
      (Date.parse(`${query.to}T00:00:00Z`) - Date.parse(`${query.from}T00:00:00Z`)) / 86_400_000 <=
      MAX_WINDOW_DAYS,
    { message: 'window_too_long', path: ['to'] },
  )
  .meta({ id: 'VenueCalendarQuery' });

export const venueCalendarItemSchema = z
  .object({
    /** Kveldens id for et arrangement, forespørselens for et hold eller en forespørsel. */
    id: z.uuid(),
    kind: z.enum(VENUE_CALENDAR_ITEM_KINDS),
    date: z.iso.date(),
    roomId: z.uuid(),
    roomName: z.string(),
    /** Tittel, innslag eller arrangør — det kilden har. Null når ingenting er satt. */
    title: z.string().nullable(),
    /** Kveldens status, eller forespørselens (`hold`, `new`). */
    status: z.string(),
    holdRank: z.number().int().nullable(),
    doorsTime: z.string().nullable(),
    /**
     * Hva raden gjør med datoen (QA15-S I3), etter den samme regelen markedet og konfliktvarselet
     * bruker: `booked` opptatt, `hold` holdt (også et arrangement på Hold eller Forespurt),
     * `waiting` en ubesvart forespørsel, `free` et avlyst eller avslått arrangement.
     */
    occupancy: z.enum(['booked', 'hold', 'waiting', 'free']),
    /** QA16 298.5: den andre parten (arrangøren som spurte, byrået budet gikk til) og innslaget. */
    who: z.string().nullable().optional(),
    act: z.string().nullable().optional(),
  })
  .meta({ id: 'VenueCalendarItem' });

export const venueCalendarResponseSchema = z
  .object({
    items: z.array(venueCalendarItemSchema),
    /** Sant når vinduet traff taket (500). Skjermen sier det. */
    truncated: z.boolean(),
  })
  .meta({ id: 'VenueCalendarResponse' });

export type VenueRentPackageDto = z.infer<typeof venueRentPackageDtoSchema>;
export type VenueRentPackageList = z.infer<typeof venueRentPackageListSchema>;
export type CreateVenueRentPackageRequest = z.infer<typeof createVenueRentPackageRequestSchema>;
export type UpdateVenueRentPackageRequest = z.infer<typeof updateVenueRentPackageRequestSchema>;
export type VenueCalendarQuery = z.infer<typeof venueCalendarQuerySchema>;
export type VenueCalendarItem = z.infer<typeof venueCalendarItemSchema>;
export type VenueCalendarResponse = z.infer<typeof venueCalendarResponseSchema>;
